/**
 * Dealer Performance Page — Merged Best of Both Builds
 * 
 * Features from old build: Volume-weighted scoring, Revenue at Risk, FPD Exposure, Dashboard Goal, ranked dealer cards
 * Features from current build: Current data sources (trpc.dignifi.*), scatter plot, search table
 */

import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Building, TrendingUp, TrendingDown, Users, AlertTriangle, 
  Star, Loader2, Search, DollarSign, Target, Trophy, Shield 
} from "lucide-react";
import { useOrganizationContext } from "@/hooks/useOrganization";
import { trpc } from "@/lib/trpc";
import { useState, useMemo } from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

export default function DealerPerformance() {
  const { organizationName, isLoading: orgLoading } = useOrganizationContext();
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch real data from API
  const { data: dealerData, isLoading: dealersLoading } = trpc.dignifi.dealerPerformance.useQuery();
  const { data: topDealers, isLoading: topLoading } = trpc.dignifi.topDealers.useQuery({ limit: 10, minLoans: 20 });
  const { data: worstDealers, isLoading: worstLoading } = trpc.dignifi.worstDealers.useQuery({ limit: 10, minLoans: 20 });
  const { data: fpdSummary, isLoading: fpdLoading } = trpc.dignifi.fpdSummary.useQuery();

  const isLoading = orgLoading || dealersLoading || topLoading || worstLoading || fpdLoading;

  // Calculate comprehensive dealer metrics with VOLUME-WEIGHTED approach
  const dealerMetrics = useMemo(() => {
    if (!dealerData) return [];
    return dealerData.map(dealer => {
      const totalLoans = Number(dealer.loan_count || 0);
      const totalPrincipal = Number(dealer.total_principal || 0);
      const fpdRate = Number(dealer.fpd_rate || 0);
      const fpdCount = Number(dealer.fpd_count || 0);
      const autopayRate = Number(dealer.autopay_rate || 0);
      const avgLoanSize = totalLoans > 0 ? totalPrincipal / totalLoans : 0;

      // Revenue at Risk: loans without autopay protection
      const loansWithoutAutopay = Math.round(totalLoans * (1 - autopayRate / 100));
      const revenueAtRisk = avgLoanSize * loansWithoutAutopay;

      // Estimated FPD exposure based on actual FPD rate for non-autopay
      const estimatedFPDExposure = revenueAtRisk * (fpdRate / 100);

      // Volume-weighted performance score
      const volumeWeight = Math.min(totalLoans / 100, 1);
      const performanceScore = Math.round((100 - fpdRate) * (0.7 + 0.3 * volumeWeight));

      // Risk Level based on BOTH FPD rate AND volume
      let riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
      if (fpdRate <= 30) {
        riskLevel = 'Low';
      } else if (fpdRate <= 50) {
        riskLevel = totalLoans >= 50 ? 'Medium' : 'Low';
      } else if (fpdRate <= 70) {
        riskLevel = totalLoans >= 50 ? 'High' : 'Medium';
      } else {
        riskLevel = totalLoans >= 30 ? 'Critical' : 'High';
      }

      return {
        dealer: dealer.dealer,
        totalLoans,
        totalPrincipal,
        avgLoanSize,
        fpdRate,
        fpdCount,
        autopayRate,
        performanceScore,
        riskLevel,
        revenueAtRisk,
        estimatedFPDExposure,
        loansWithoutAutopay,
      };
    });
  }, [dealerData]);

  // Filter to meaningful volume dealers
  const significantDealers = useMemo(() => 
    dealerMetrics.filter(d => d.totalLoans >= 20 || d.totalPrincipal >= 500000),
    [dealerMetrics]
  );

  // Executive Summary Stats
  const summary = useMemo(() => {
    const totalDealers = significantDealers.length;
    const totalVolume = significantDealers.reduce((sum, d) => sum + d.totalLoans, 0);
    const totalRevenue = significantDealers.reduce((sum, d) => sum + d.totalPrincipal, 0);
    const totalRevenueAtRisk = significantDealers.reduce((sum, d) => sum + d.revenueAtRisk, 0);
    const totalFPDExposure = significantDealers.reduce((sum, d) => sum + d.estimatedFPDExposure, 0);
    const highRiskCount = significantDealers.filter(d => d.riskLevel === 'High' || d.riskLevel === 'Critical').length;
    const avgFPDRate = totalVolume > 0
      ? (significantDealers.reduce((sum, d) => sum + (d.fpdRate * d.totalLoans), 0) / totalVolume).toFixed(1)
      : '0';

    return { totalDealers, totalVolume, totalRevenue, totalRevenueAtRisk, totalFPDExposure, highRiskCount, avgFPDRate, allDealers: dealerMetrics.length };
  }, [significantDealers, dealerMetrics]);

  // Filter dealers by search
  const filteredDealers = useMemo(() => {
    const sorted = [...significantDealers].sort((a, b) => b.performanceScore - a.performanceScore);
    if (!searchTerm) return sorted;
    return sorted.filter(d => d.dealer.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [significantDealers, searchTerm]);

  // Scatter plot data
  const scatterData = useMemo(() => {
    return significantDealers.map(d => ({
      x: d.totalLoans,
      y: d.fpdRate,
      z: d.totalPrincipal,
      name: d.dealer,
    }));
  }, [significantDealers]);

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toFixed(0)}`;
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading dealer performance data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dealer Performance & Business Impact</h1>
          <p className="text-muted-foreground mt-2">
            Volume-weighted dealer analysis focusing on revenue protection and FPD prevention
          </p>
          <div className="mt-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-sm font-medium text-primary">
              <strong>Dashboard Goal:</strong> Identify high-volume dealers driving revenue growth and pinpoint FPD exposure risks requiring immediate intervention. Focus on dealers with 20+ loans or $500K+ volume for maximum business impact.
            </p>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Currently tracking {summary.totalDealers} of {summary.allDealers} total dealers meeting volume criteria
          </p>
        </div>

        {/* Executive Summary Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue Tracked</CardTitle>
              <DollarSign className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(summary.totalRevenue)}</div>
              <p className="text-xs text-muted-foreground">
                {summary.totalVolume.toLocaleString()} loans from {summary.totalDealers} dealers
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Revenue at Risk</CardTitle>
              <AlertTriangle className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-500">{formatCurrency(summary.totalRevenueAtRisk)}</div>
              <p className="text-xs text-muted-foreground">
                Loans without autopay protection
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Estimated FPD Exposure</CardTitle>
              <Target className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-500">{formatCurrency(summary.totalFPDExposure)}</div>
              <p className="text-xs text-muted-foreground">
                Based on dealer-level FPD rates
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">High-Risk Dealers</CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.highRiskCount}</div>
              <p className="text-xs text-muted-foreground">
                Dealers with significant FPD exposure
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Top Performers & At-Risk Dealers */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Top Performers */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Top 10 High-Volume Performers
              </CardTitle>
              <CardDescription>
                Dealers with strong performance AND significant loan volume
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {topDealers?.map((dealer, idx) => {
                  const fpdRate = Number(dealer.fpd_rate || 0);
                  return (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/20 rounded-lg hover:bg-green-500/20 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="text-2xl font-bold text-yellow-500">#{idx + 1}</div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{dealer.dealer || 'Unknown'}</p>
                          <p className="text-sm text-muted-foreground">
                            {dealer.loan_count} loans &middot; {formatCurrency(Number(dealer.total_principal || 0))} volume
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-green-400">{fpdRate}%</div>
                        <p className="text-xs text-muted-foreground">FPD rate</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* At-Risk Dealers */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Top 10 FPD Exposure Risks
              </CardTitle>
              <CardDescription>
                High-volume dealers with high FPD rates — highest dollar-value risk
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {worstDealers?.map((dealer, idx) => {
                  const fpdRate = Number(dealer.fpd_rate || 0);
                  const totalLoans = Number(dealer.loan_count || 0);
                  const totalPrincipal = Number(dealer.total_principal || 0);
                  const avgLoan = totalLoans > 0 ? totalPrincipal / totalLoans : 0;
                  const exposure = avgLoan * totalLoans * (fpdRate / 100);
                  
                  return (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{dealer.dealer || 'Unknown'}</p>
                        <p className="text-sm text-muted-foreground">
                          {dealer.loan_count} loans &middot; {formatCurrency(totalPrincipal)} volume
                        </p>
                        <p className="text-xs text-red-400 font-medium mt-1">
                          {formatCurrency(exposure)} FPD exposure
                        </p>
                      </div>
                      <div className="text-right flex items-center gap-3">
                        <div>
                          <div className="text-2xl font-bold text-red-400">{fpdRate}%</div>
                          <Badge variant="destructive" className="mt-1">
                            {fpdRate > 70 ? 'Critical' : fpdRate > 50 ? 'High' : 'Medium'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Loan Volume vs FPD Scatter Plot */}
        <Card>
          <CardHeader>
            <CardTitle>Loan Volume vs FPD Rate by Dealer</CardTitle>
            <CardDescription>Dealers with 20+ loans. Higher volume dealers should have lower FPD rates.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis 
                    type="number" 
                    dataKey="x" 
                    name="Loan Count" 
                    stroke="#888"
                    label={{ value: 'Loan Count', position: 'bottom', fill: '#888', offset: 0 }}
                  />
                  <YAxis 
                    type="number" 
                    dataKey="y" 
                    name="FPD Rate" 
                    unit="%" 
                    stroke="#888"
                    label={{ value: 'FPD Rate (%)', angle: -90, position: 'insideLeft', fill: '#888' }}
                  />
                  <ZAxis type="number" dataKey="z" range={[50, 400]} name="Principal" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                    formatter={(value: number, name: string) => {
                      if (name === 'FPD Rate') return [`${value}%`, name];
                      if (name === 'Principal') return [`$${value.toLocaleString()}`, name];
                      return [value, name];
                    }}
                    labelFormatter={(label) => {
                      const point = scatterData.find(d => d.x === label);
                      return point?.name || '';
                    }}
                  />
                  <Scatter name="Dealers" data={scatterData}>
                    {scatterData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.y <= 40 ? '#22c55e' : entry.y <= 60 ? '#f59e0b' : '#ef4444'} 
                      />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span>Low Risk (40% FPD)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span>Medium Risk (40-60% FPD)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span>High Risk (&gt;60% FPD)</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* High-Volume Dealer Scorecard Table */}
        <Card>
          <CardHeader>
            <CardTitle>High-Volume Dealer Scorecard</CardTitle>
            <CardDescription>
              Complete metrics for dealers with 20+ loans or $500K+ volume — ranked by performance score
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search dealers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium">Dealer</th>
                    <th className="text-right py-3 px-4 font-medium">Loan Volume</th>
                    <th className="text-right py-3 px-4 font-medium">Total Amount</th>
                    <th className="text-right py-3 px-4 font-medium">FPD Rate</th>
                    <th className="text-right py-3 px-4 font-medium">FPD Exposure</th>
                    <th className="text-right py-3 px-4 font-medium">Risk Level</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDealers.slice(0, 50).map((dealer, index) => (
                    <tr key={index} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                      <td className="py-3 px-4 font-medium truncate max-w-[250px]" title={dealer.dealer}>
                        {dealer.dealer}
                      </td>
                      <td className="text-right py-3 px-4 font-medium">{dealer.totalLoans}</td>
                      <td className="text-right py-3 px-4 font-medium">{formatCurrency(dealer.totalPrincipal)}</td>
                      <td className="text-right py-3 px-4">
                        <Badge 
                          variant={dealer.fpdRate <= 40 ? 'default' : dealer.fpdRate <= 60 ? 'secondary' : 'destructive'}
                          className={dealer.fpdRate <= 40 ? 'bg-green-500 hover:bg-green-600' : ''}
                        >
                          {dealer.fpdRate}%
                        </Badge>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className={`font-medium ${
                          dealer.estimatedFPDExposure > 50000 ? 'text-red-500' :
                          dealer.estimatedFPDExposure > 20000 ? 'text-orange-500' :
                          'text-muted-foreground'
                        }`}>
                          {formatCurrency(dealer.estimatedFPDExposure)}
                        </span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <Badge 
                          variant={
                            dealer.riskLevel === 'Low' ? 'default' :
                            dealer.riskLevel === 'Medium' ? 'secondary' :
                            'destructive'
                          }
                          className={dealer.riskLevel === 'Low' ? 'bg-green-500 hover:bg-green-600' : ''}
                        >
                          {dealer.riskLevel}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {filteredDealers.length > 50 && (
              <p className="text-sm text-muted-foreground mt-4 text-center">
                Showing 50 of {filteredDealers.length} dealers. Use search to filter.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
